import React, { useEffect, useState } from 'react'
import { BulkInstructorItem, BulkInstructors, GroupedRadioLabel } from './style'
import { useIntl } from 'react-intl'
import Radio from '@components/Radio'
import { StoreProps } from '@interfaces/StoreState'
import { useSelector } from 'react-redux'
import Skeleton from 'react-loading-skeleton'
import { useFMIBulkForm } from '../../BulkForm/Form/useFMIBulkForm'
import { useRouter } from 'next/router'
import Spacer from '@berlitz/spacer'
import { BulkCalendar, BulkCalendarWrapper, BulkLeftNav, FMIBulkActions, FMIBulkAlert, FMIBulkSelect } from '../style'
import Alert from '@berlitz/alert'
import Select from '@components/Select'
import { getMessages } from './constants'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'

interface IInstructors {
  children?: React.ReactNode
  defaultInstructorId: string
  loading?: boolean
  disabled?: boolean
  isMobile?: boolean
  onChange: (InstructorId: string) => void
}

const Instructors: React.FC<IInstructors> = ({
  defaultInstructorId = '',
  loading = false,
  disabled = false,
  isMobile = false,
  onChange = () => {},
  children,
}) => {
  const intl = useIntl()
  const router = useRouter()
  const { isBulkOnlyFeature } = useFMIBulkForm()
  const {
    selectedInstructors = [],
    favouriteInstructors = [],
    pastInstructors = [],
    mostAvailableInstructors = [],
  } = useSelector(({ fmiBulkForm }: StoreProps) => ({
    selectedInstructors: fmiBulkForm.instructors || [],
    favouriteInstructors: fmiBulkForm.favouriteInstructors || [],
    pastInstructors: fmiBulkForm.pastInstructors || [],
    mostAvailableInstructors: fmiBulkForm.mostAvailableInstructors || [],
  }))
  const messages = getMessages()
  const [selectedInstructorId, setSelectedInstructorId] = useState<string | undefined>()

  useEffect(() => {
    if (isBulkOnlyFeature && selectedInstructors.length) {
      setSelectedInstructorId(selectedInstructors?.[0]?.InstructorId)
    }

    if (isBulkOnlyFeature && !selectedInstructors.length) {
      router.push('/scheduling/instructors')
    }
  }, [])

  if (!isBulkOnlyFeature) {
    return null
  }

  const FMIBulkWarning = ({ classes = 3 }) => (
    <Alert type="warning">
      <div className="label">
        {intl.formatMessage(messages.instructorsWarning, {
          classes,
        })}
      </div>
    </Alert>
  )

  if (isMobile) {
    const populateOptions = ({ InstructorId, Name }: InstructorSearchResultInfo) => ({
      label: Name,
      value: InstructorId,
    })

    return (
      <>
        <FMIBulkActions>
          <FMIBulkSelect>
            <div className="label">{intl.formatMessage(messages.Instructors)}</div>

            {loading ? (
              <Skeleton height="46px" style={{ maxWidth: '300px', minWidth: '215px' }} />
            ) : (
              <Select
                onChange={onChange}
                options={[
                  ...(favouriteInstructors.length
                    ? [
                        {
                          label: intl.formatMessage(messages.Favourite),
                          value: intl.formatMessage(messages.Favourite),
                          disabled: true,
                        },
                        ...favouriteInstructors.map(populateOptions),
                      ]
                    : []),
                  ...(pastInstructors.length
                    ? [
                        {
                          label: intl.formatMessage(messages.Past),
                          value: intl.formatMessage(messages.Past),
                          disabled: true,
                        },
                        ...pastInstructors.map(populateOptions),
                      ]
                    : []),
                  ...(mostAvailableInstructors.length
                    ? [
                        {
                          label: intl.formatMessage(messages.MostAvailable),
                          value: intl.formatMessage(messages.MostAvailable),
                          disabled: true,
                        },
                        ...mostAvailableInstructors.map(populateOptions),
                      ]
                    : []),
                ]}
                disableMargin
                value={defaultInstructorId}
                responsive
                disabled={disabled}
                fullWidth
              />
            )}
          </FMIBulkSelect>

          <FMIBulkAlert>
            <FMIBulkWarning />
          </FMIBulkAlert>
        </FMIBulkActions>

        <Spacer size="md" />
      </>
    )
  }

  return (
    <BulkCalendarWrapper>
      <BulkLeftNav>
        <FMIBulkAlert>
          <FMIBulkWarning />
        </FMIBulkAlert>

        <BulkInstructors>
          <h1>{intl.formatMessage(messages.Instructors)}</h1>

          {loading ? (
            Array.from(Array(5).keys()).map((_, index) => (
              <BulkInstructorItem key={`loader-${index}`}>
                <Skeleton height="24px" style={{ width: '100%' }} />
              </BulkInstructorItem>
            ))
          ) : (
            <Radio.Group
              value={selectedInstructorId}
              onChange={(val) => {
                const InstructorId = val.target.value
                setSelectedInstructorId(InstructorId)
                onChange(InstructorId)
              }}
              name="instructor-id"
            >
              {favouriteInstructors.length ? (
                <>
                  <GroupedRadioLabel>
                    <span>{intl.formatMessage(messages.Favourite)}</span>
                    <div />
                  </GroupedRadioLabel>
                  {favouriteInstructors.map(({ InstructorId, Name }) => (
                    <Radio.Radio key={`favourite-instructors-${InstructorId}`} value={InstructorId} disabled={disabled}>
                      <BulkInstructorItem>{Name}</BulkInstructorItem>
                    </Radio.Radio>
                  ))}
                </>
              ) : null}

              {pastInstructors.length ? (
                <>
                  <GroupedRadioLabel>
                    <span>{intl.formatMessage(messages.Past)}</span>
                    <div />
                  </GroupedRadioLabel>
                  {pastInstructors.map(({ InstructorId, Name }) => (
                    <Radio.Radio key={`past-instructors-${InstructorId}`} value={InstructorId} disabled={disabled}>
                      <BulkInstructorItem>{Name}</BulkInstructorItem>
                    </Radio.Radio>
                  ))}
                </>
              ) : null}

              {mostAvailableInstructors.length ? (
                <>
                  <GroupedRadioLabel>
                    <span>{intl.formatMessage(messages.MostAvailable)}</span>
                    <div />
                  </GroupedRadioLabel>
                  {mostAvailableInstructors.map(({ InstructorId, Name }) => (
                    <Radio.Radio
                      key={`most-available-instructors-${InstructorId}`}
                      value={InstructorId}
                      disabled={disabled}
                    >
                      <BulkInstructorItem>{Name}</BulkInstructorItem>
                    </Radio.Radio>
                  ))}
                </>
              ) : null}
            </Radio.Group>
          )}
        </BulkInstructors>
      </BulkLeftNav>

      <BulkCalendar>{children}</BulkCalendar>
    </BulkCalendarWrapper>
  )
}

export default Instructors
